/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import "../css/CreditPlanDialog.css";
import { PAYMENT_STATUS, PURCHASE_TYPE } from "utils/constants";
import ROUTE_PATHS from "utils/routePaths";
import { useNavigate } from "react-router-dom";
import coinsIcon from "../../assets/images/credit-paln-coins-icon.svg";
import letsDoItIcon from "../../assets/images/lets-do-it-icon.svg";
import { useStorage } from "services/StorageProvider";
import useWSMApi from "services/api/useWSMApi";
import { useMutation } from "@tanstack/react-query";

interface CreditPlanDialogProps {
  planData: any;
  open: boolean;
  onClose: () => void;
}

const CreditPlanDialog: React.FC<CreditPlanDialogProps> = ({
  planData,
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const { userProfileData } = useStorage();
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string>("");
  const { createPayment, verifyPayment } = useWSMApi();

  const purchaseCreditPlanMutation = useMutation({
    mutationFn: (data: any) => createPayment(data),
    onSuccess: (response, obj) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: obj.product.total_price,
        currency: obj.currency,
        name: "The Foundry", //Name Shown in Razorpay Form
        description: obj.product.description,
        order_id: response.id, // This is the order_id created in the backend
        prefill: {
          name: obj.customer.name,
          email: obj.customer.email,
          contact: obj.customer.contact,
        },
        theme: {
          color: "#1C1C1E",
        },
        handler: async function (paymentResponse: any) {
          setPaymentId(paymentResponse.razorpay_payment_id);
          verifyPaymentMutation.mutate({
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => {
            if (!paymentStatus) {
              setPaymentStatus(PAYMENT_STATUS.CANCELLED);
            }
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    },
  });

  const verifyPaymentMutation = useMutation({
    mutationFn: (data: any) => verifyPayment(data),
    onSuccess: (response) => {
      setPaymentStatus(response.status);
    },
    onError: () => {
      setPaymentStatus(PAYMENT_STATUS.FAILED);
    },
  });

  useEffect(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      const paymentData = {
        title: planData?.title,
        type: PURCHASE_TYPE.CREDIT,
        serviceType: planData?.credit_service_types[0],
      };
      onClose();
      localStorage.setItem("paymentSuccessData", JSON.stringify(paymentData));
      navigate(ROUTE_PATHS.WSM_ALL_SET);
    }
  }, [paymentStatus, paymentId, navigate]);

  const handlePurchaseClick = () => {
    const planPrice = Number(planData?.credit_price) + getGSTPrice();
    const amountInSubunits = Math.round(planPrice * 100);
    const passedObj = {
      amount: planPrice,
      currency: "INR",
      customer: {
        name: `${userProfileData?.first_name} ${userProfileData?.last_name}`,
        email: userProfileData?.email,
        contact: userProfileData?.mobile_number,
      },
      product: {
        total_price: amountInSubunits, // Amount in paise
        credit_plan_id: planData?.id,
        description: planData?.title,
        price: planData?.credit_price,
        with_gst_price: getGSTPrice(),
        credit: planData?.total_credits,
        validity: planData?.credit_validity,
      },
    };
    setPaymentStatus(null);
    purchaseCreditPlanMutation.mutate(passedObj);
  };

  const getGSTPrice = () => {
    const gstPrice =
      (Number(planData?.credit_price) * planData?.credit_gst) / 100;
    return Math.floor(gstPrice);
  };

  const getServiceTypeParam = (serviceType: any): string => {
    switch (serviceType) {
      case "DF_WORKSPACE":
        return "dfw_credits";

      case "MB_STUDIO":
        return "mbs_credits";

      case "EVENTS":
        return "event_credits";

      case "WORKSHOP":
        return "workshop_credits";

      default:
        return "";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        className: "credit-plan-dialog-container",
      }}
      BackdropProps={{
        className: "credit-plan-dialog-backdrop",
      }}
    >
      <DialogContent sx={{ padding: 0, margin: 0 }}>
        <div className="credit-plan-dialog-header">Acknowledgement</div>
        <div className="credit-plan-dialog-containt-container">
          <span>{planData?.title}</span>
          <div className="credit-container">
            {planData?.credit_service_types?.length > 0 && (
              <div className="sub-container">
                <img src={coinsIcon} alt="coins-icon" />
                {`${
                  planData?.[
                    getServiceTypeParam(planData?.credit_service_types[0])
                  ]
                } ${planData?.total_credits > 1 ? "Credits" : "Credit"}`}
              </div>
            )}
          </div>
          <span className="price-text">{`INR ${planData?.credit_price?.toLocaleString(
            "en-US",
          )}`}</span>
        </div>
        <div
          className="credit-plan-dialog-button"
          onClick={handlePurchaseClick}
        >
          <div>Buy Now</div>
          <img src={letsDoItIcon} alt="lets-do-it-logo" />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreditPlanDialog;
